import React, { useState, useEffect, createRef, forwardRef, useRef, useCallback, memo, useContext, useImperativeHandle } from 'react';
import '../../assets/styles/form.scss';
import { TextDefaultElement, PhoneDefaultElement, InputGuestEmails, SelectDropdown, Checkbox, CustomShortText, CustomLongText, MultipleCheckbox, RadioButton } from './inputs';
import Axios from 'axios';
import Confirmation from './Confirmation';
import PaymentDetail from '../payment/PaymentDetail';
import Paypal from '../payment/Paypal';
import PaymentForm from './PaymentStripeForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ModalContext } from '../../contexts/modalContext';
import { LoadingContext } from '../../contexts/loadingContext';
import { ToastContext } from '../../contexts/toastContext';
import { Colors } from '../../constants';
import useWindowDimensions from '../../custom_hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';

const URL_PARAMS = new URLSearchParams(window.location.search);

const INITIAL_STATES = {
  ref:null,
  uid:null,
  isReschedule:false,
  color:null,
  locationSearch: null,
  formElements: [],
  defaultElements: [],
  show: false,
  countryCode: '',
  permittedCountries: [],
  customDisplayedCountries: [],
  defaultCountry: '',
  acceptPayment: null,
  timezoneName: '',
  times: null,
  formURL: '',
  method: '',
  facebookPixel: null,
  googleAnalytics: null,
  googleAnalytics4: null,
  clearDay: undefined,
  handleBackButton: undefined,
  name: '',
  location: '',
  isCancelMeeting: false,
  callbackInfoMessage: undefined,
  rescheduleURL: null,
  cancelURL: null,
  paymentSettings: null,
  chargeDetail: null,
  handleBackAfterConfirmation: undefined,
  hideHeaderText: undefined,
  handleFormSubmitRef: null,
  handleConfirmButton: undefined,
  languageCode: 'en',
  isoTime: false,
  time_interval: 0,
  locationOptions: [],
  currentDuration: 0,
  scheduleLocation: ''
};

const FormField = forwardRef((props, ref) =>{
  const [ formRef ] = useState(ref);
  const [ isLoadingPage, setIsLoadingPage ] = useState(true);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ propValues, setPropValues ] = useState(INITIAL_STATES);

  const [ modalConfirmBgColor, setModalConfirmBgColor ] = useState('');
  const [ buttonFontColor, setButtonFontColor ] = useState(`#${Colors.white}`);
  const [ showConfirmation, setShowConfirmation ] = useState(false);
  const [ elements, setElements ] = useState(null);
  const elementRefs = useRef({});
  const reasonChangeRef = useRef(null);
  const [ acceptCancel, setAcceptCancel ] = useState(false);
  const [ selectedLocation, setSelectedLocation ] = useState(null);

  // internationalization - translation
  const { t } = useTranslation();

  // schedule created
  const [ schedule, setSchedule ] = useState(null);
  const scheduleRef = useRef(null);

  // for payment
  const [ stripePromise, setStripePromise ] = useState(null);

  // modal to show confirmation
  let { handleModal, hideModal } = useContext(ModalContext);

  // to show loading overlay
  const { showLoading, hideLoading } = useContext(LoadingContext);

  // to show error toastr
  const { showError, toastList, autoDeleteTime } = useContext(ToastContext);


  // if phone number element exists
  const [ isPhoneNumberElementExists, setIsPhoneNumberElementExists ] = useState(false);

  useEffect(() => {
    if(props.show){
      setPropValues(props);
      setModalConfirmBgColor(props.color);

      if(props.acceptPayment) setIsLoading(true);
    }
  },[props]);

  useEffect(() => {
    if(propValues){
      const params  = new URLSearchParams(propValues.locationSearch);

      function generateFormElementInputField(data, languageOptions = []){

        let arrayOfElements = [];

        data.forEach((element, index) => {
          let refName = '';
          if(element['object'] === 'checkbox'){
            refName = `check${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <Checkbox
                key={index}
                ref={elementRefs.current[refName]}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }
          else if(element['object'] === 'dropdown'){
            refName = `box2${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <SelectDropdown
                key={index}
                ref={createRef()}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }
          else if(element['object'] === 'checkboxes'){
            refName = `box2${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <MultipleCheckbox
                key={index}
                ref={createRef()}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }
          else if(element['object'] === 'radio_button'){
            refName = `box2${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <RadioButton
                key={index}
                ref={createRef()}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }
          else if(element['object'] === 'short_text'){
            refName = `box2${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <CustomShortText
                key={index}
                ref={elementRefs.current[refName]}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }
          else if(element['object'] === 'long_text'){
            refName = `box2${index}`;
            elementRefs.current[refName] = createRef(refName);
            arrayOfElements.push(
              <CustomLongText
                key={index}
                ref={elementRefs.current[refName]}
                index={index}
                data={element}
                URL_PARAMS={URL_PARAMS}
                color={propValues.color} />
            );
          }

        });

        if (Object.keys(propValues.locationOptions).length > 0 && propValues.location === 'Ask Invitee') {
          let refName = 'dropdown-location-options';
          elementRefs.current[refName] = createRef(refName);
          let paramsInputNumber = params.get('number');
          arrayOfElements.push(
              <SelectDropdown
                key={'location-options'}
                ref={elementRefs.current[refName]}
                URL_PARAMS={URL_PARAMS}
                data={propValues.locationOptions}
                index={'language-options'}
                color={propValues.color}
                elementRefs={elementRefs}
                countryCode={propValues.countryCode}
                number={paramsInputNumber || ''}
                permittedCountries={propValues.permittedCountries}
                customDisplayedCountries={propValues.customDisplayedCountries}
                defaultCountry={propValues.defaultCountry}
                handleOnChange={(number) => {
                  elementRefs.current[refName].current = number;
                }}
                isPhoneNumberElementExists={isPhoneNumberElementExists} />
            );
        }

        if (propValues.location === 'Phone Call' && !isPhoneNumberElementExists) {
          let refName = 'location-options-phone-call';
          let paramsInputNumber = params.get('number');
          elementRefs.current[refName] = createRef(refName);
          arrayOfElements.push(
            <PhoneDefaultElement
              key={'location-options-phone-call'}
              ref={elementRefs.current[refName]}
              countryCode={propValues.countryCode}
              index={'location-options-phone-call'}
              number={paramsInputNumber || ''}
              permittedCountries={propValues.permittedCountries}
              customDisplayedCountries={propValues.customDisplayedCountries}
              defaultCountry={propValues.defaultCountry}
              handleOnChange={(number) => elementRefs.current[refName].current = number}
              color={propValues.color} />
          )
        }

        return arrayOfElements;
      }


      function generateDefaultElementInputField(data){
        let paramsInputName = params.get('name');
        let paramsInputEmail = params.get('email');
        let paramsInputNumber = params.get('number');
        let paramsInputGuestEmails = params.get('guest_emails');

        let arrayOfElements = [];

        data.forEach((element, index) => {
          let refName = `box1${index}`;
          if(element === 'name'){
            elementRefs.current[refName] = createRef(refName);
            elementRefs.current[refName].current = paramsInputName;
            arrayOfElements.push(
              <TextDefaultElement
                key={index}
                ref={elementRefs.current[refName]}
                index={index}
                data={'name'}
                defaultValue={paramsInputName}
                color={propValues.color} />
            );
          }
          else if(element === 'email'){
            elementRefs.current[refName] = createRef(refName);
            elementRefs.current[refName].current = paramsInputEmail;
            arrayOfElements.push(
              <TextDefaultElement
                key={index}
                ref={elementRefs.current[refName]} i
                ndex={index}
                data={'email'}
                defaultValue={paramsInputEmail}
                color={propValues.color} />
            );
          }
          else if(element === 'guest_emails'){
            elementRefs.current[refName] = createRef(refName);
            elementRefs.current[refName].current = paramsInputGuestEmails;
            arrayOfElements.push(
              <InputGuestEmails
                key={index}
                ref={elementRefs.current[refName]}
                index={index}
                data={'guest_emails'}
                defaultValue={paramsInputGuestEmails}
                color={propValues.color}
              />
            );
          }
          else if(element === 'number'){
            elementRefs.current[refName] = createRef(refName);
            elementRefs.current[refName].current = paramsInputNumber;
            arrayOfElements.push(
              <PhoneDefaultElement
                key={index}
                ref={elementRefs.current[refName]}
                countryCode={propValues.countryCode}
                index={index}
                number={paramsInputNumber || ''}
                permittedCountries={propValues.permittedCountries}
                customDisplayedCountries={propValues.customDisplayedCountries}
                defaultCountry={propValues.defaultCountry}
                handleOnChange={(number) => {
                  elementRefs.current[refName].current = number;
                }}
                color={propValues.color} />
            );
            setIsPhoneNumberElementExists(true);
          }

        });

        return arrayOfElements;
      }

      if(propValues.show){
        if(!propValues.isReschedule && !propValues.isCancelMeeting && (propValues.defaultElements || propValues.formElements)){
          let fields = [];
          fields.push(generateDefaultElementInputField(propValues.defaultElements));
          fields.push(generateFormElementInputField(propValues.formElements, propValues.locationOptions));
          setElements(fields);
          // Stripe
          if(propValues.acceptPayment === 'stripe' && propValues?.chargeDetail?.stripe?.published_key){
            setStripePromise(loadStripe(propValues?.chargeDetail?.stripe?.published_key));
          } else {
            setIsLoading(false);
          }
        }
        setIsLoadingPage(false);
        if(propValues.isReschedule || propValues.isCancelMeeting){
          setTimeout(() => {
            setIsLoading(false);
          }, 250);
        }
      }
    }

  },
  // eslint-disable-next-line
  [propValues]);

  useEffect(() => {
    if(propValues.isCancelMeeting)
      setShowConfirmation(false);
  }, [propValues.isCancelMeeting]);

  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  // when loading -> submitting form
  useEffect(() => {
    if(isLoading){
      showLoading(propValues.color);
    }
    else{
      hideLoading();
    }
  },
  // eslint-disable-next-line
  [isLoading]);

  // function to determine the button color when the mouse is over
  const modalConfirmButtonOver=()=>{
    setModalConfirmBgColor(`${propValues.color}10`);
    setButtonFontColor(`${propValues.color}`);
  }

  // function to determine the button color when the mouse is out
  const modalConfirmButtonOut=()=>{
    setModalConfirmBgColor(propValues.color);
    setButtonFontColor(`#${Colors.white}`);
  }

  // function to submit form
  const submitForm = async(event) => {
    try{
      event.preventDefault();
      // event.persist();

      if(propValues.isCancelMeeting){
        // cancel meeting
        handleModal(t('Confirmation'),
            `${t('Are you sure want to cancel the meeting')}?`,
            cancelMeeting,
            { cancel: t('No'), submit: t('Yes') },
            true,
            propValues.color );
      }
      else{

        // Check required checkboxes
        if (check_required_checkboxes()) { return }

        let data = populateFormData();
        if (data.status === 'success') {
          if(Object.keys(data.schedules).length > 0 && !propValues.isReschedule && !propValues.isCancelMeeting){
            setIsLoading(true);
            // create new appointment
            await createNewAppointment(data);
          }
          else if(propValues.isReschedule){
            // reschedule
            await handleModal(t('Confirmation'),
              `${t('Are you sure want to reschedule the meeting')}?`,
              () => rescheduleMeeting(data),
              { cancel: t('No'), submit: t('Yes') },
              true,
              propValues.color );
          }
        }
        else {
          showError(data.message);
        }
      }
    }
    catch (err) {
      showError(err.message);
    }
  };

  const check_required_checkboxes = () => {
    const multiple_checkbox_required = document.getElementsByClassName(`multiple-checkboxes cb-required`)
    for (let i = 0; i < multiple_checkbox_required.length; i++) {
      let checked = multiple_checkbox_required[i].querySelectorAll('input.checkbox__field:checked')
      if (!checked.length) {
        console.log(multiple_checkbox_required[i].querySelector('span.required'))
        let required_message_elament = multiple_checkbox_required[i].querySelector('span.required')
        required_message_elament.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
          required_message_elament.innerHTML = ` * <span style='color: red;'> Please select one of these options.</span>`;
        }, 500);
        setTimeout(() => {
          required_message_elament.innerHTML = " *"
        }, 5000);
        return true
      }
    }
  }

  const populateQualificationData = () => {
    let searchParams = new URLSearchParams(window.location.search);
    let questions = searchParams.get('q')?.split(",");
    let answers = searchParams.get('a')?.split(",");
    let data = {};
    if (questions && answers) {
      for (let i = 0; i < questions.length; i++) {
        data[questions[i]] = answers[i];
      }
    }

    return data;
  };

  const populateFormData = useCallback((schedules = {}, input = {}, teams = {}) => {
    // set questions and answers for qualification
    schedules['qualification'] = populateQualificationData();
    // set timing
    schedules['timing']=propValues.times/100;
    schedules['duration'] = propValues.currentDuration;

    // set timezone
    // if(Math.sign(propValues.timezone)===1){
    //     schedules['timezone']=`Etc/GMT+${propValues.timezone}`
    // } else if(Math.sign(propValues.timezone)===0){
    //     schedules['timezone']=`Etc/GMT`
    // } else {
    //     schedules['timezone']=`Etc/GMT${propValues.timezone}`
    // }
    schedules['timezone'] = propValues.timezoneName;
    if(!propValues.isReschedule && !propValues.isCancelMeeting){
      schedules['uid']= propValues.uid;

      for(let x=0; x<propValues.defaultElements.length; x++){
        if(propValues.defaultElements[x] === 'number'){
          if(elementRefs.current[`box1${x}`].current?.state?.formattedNumber){
            schedules[propValues.defaultElements[x]]=elementRefs.current[`box1${x}`].current.state.formattedNumber.replaceAll(' ', '');
          }
          else{
            schedules[propValues.defaultElements[x]]=elementRefs.current[`box1${x}`].current;
          }
        } else{
          schedules[propValues.defaultElements[x]]=elementRefs.current[`box1${x}`].current.value;
        }

        if(propValues.defaultElements[x] === 'guest_emails'){
          var guestEmails = document.getElementById('guestEmails').value;
          schedules[propValues.defaultElements[x]]=guestEmails;
        }

      }

      for(let x=0;x<propValues.formElements.length;x++){
          let eleName=propValues.formElements[x]['name']
          if(propValues.formElements[x]['object']==="short_text" || propValues.formElements[x]['object']==="long_text"){
              input[eleName]=elementRefs.current[`box2${x}`].current?.value;
          } else if(propValues.formElements[x]['object']==="dropdown"){
              var z = document.getElementById(`dropdown${x}`).selectedIndex;
              var y = document.getElementById(`dropdown${x}`).options;
              if(propValues.formElements[x]['type']==="teams"){
                  teams[eleName]=y[z].text;
              } else {
                  input[eleName]=y[z].text;
              }

          } else if(propValues.formElements[x]['object']==="checkboxes"){
              var checkbox_form = document.getElementById(`checkboxes${x}`);

              var checked = []
              for (var i = 0; i < checkbox_form.childNodes.length; i++) {
                  if (checkbox_form.childNodes[i].querySelector('input').checked) {
                      checked.push(checkbox_form.childNodes[i].querySelector('input').value)
                  }
              }
              input[eleName] = checked
          } else if(propValues.formElements[x]['object']==="radio_button"){
              var checked_rb = document.getElementById(`radio_button${x}`);
              input[eleName]=checked_rb.querySelector(`input[name="radio_button${x}"]:checked`)?.value;
          } else if(propValues.formElements[x]['object']==="checkbox"){
              input[eleName]=elementRefs.current[`check${x}`].current?.checked
          }
      }

      schedules['inputs'] = input;
      // location options
      if (propValues.location === 'Ask Invitee' && Object.keys(propValues.locationOptions).length > 0 && propValues.locationOptions.content) {
        let selected = elementRefs.current['dropdown-location-options'].current.value || selectedLocation || document.getElementById('dropdownlanguage-options').value;
        if (selected === null || selected === undefined) { return { status: 'error', message: t('Location is required') } }
        setSelectedLocation(selected)
        if (selected === 'Google Meet') selected = 'hangouts';
        else if (selected === 'Phone Call') selected = 'phone';
        schedules['timesync_location'] = selected;

        // check if phone number exists for location Phone Call
        if(selected === 'phone' && !isPhoneNumberElementExists) {
          let phone_number = (elementRefs.current['location-options-phone-call'].current?.state?.formattedNumber || elementRefs.current['location-options-phone-call'].current).replaceAll(' ', '');
          if (!phone_number) {
            return { status: 'error', message: t('Phone Number is required') };
          } else {
            schedules['number'] = phone_number;
          }
        }
      } else if (propValues.location === 'Phone Call' && !isPhoneNumberElementExists) { // check for phone call
        let phone_number = (elementRefs.current['location-options-phone-call'].current?.state?.formattedNumber || elementRefs.current['location-options-phone-call'].current).replaceAll(' ', '');
        if (!phone_number) {
          return { status: 'error', message: t('Phone Number is required') }
        } else {
          schedules['number'] = phone_number;
        }
      }
    }

    return { status: 'success', schedules, input, teams }
  },
  [propValues, t, selectedLocation, isPhoneNumberElementExists]);

  const createNewAppointmentAPI = useCallback(async(data) => {
    try{
      return await Axios({
        method: propValues.method,
        url: `${propValues.formURL}?locale=${propValues.languageCode}`,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        data: data
      }).then((response) => {
        if(response.data.status){
          setSchedule(response.data.schedule);
          return { status: 'success', response: response.data }
        }
        else{
          showError(response.data.message);
          return { status: 'error', response: response.data } ;
        }
      })
    }
    catch(err) {
      showError(err.message);
    }
  },
  // eslint-disable-next-line
  [propValues]);

  // to show or hide confirmation component
  const changeShowConfirmationComponent = useCallback(() => {
    setShowConfirmation(!showConfirmation);
  },
  // eslint-disable-next-line
  [showConfirmation]);

  // to set isLoading state
  const changeIsLoading = useCallback((value = null) => {
    if(value === null){
      setIsLoading(!isLoading);
    }
    else {
      setIsLoading(value);
    }
  }, [isLoading]);

  // book a new appointment
  const createNewAppointment = async (data) => {
    try{
      const result = await createNewAppointmentAPI(data);
      if(result && result['status'] === 'success'){
        changeShowConfirmationComponent();
        successCreateAppointment();
      }
      setIsLoading(false);
    }
    catch(err) {
      showError(err.message);
    }
  };

  // reschedule
  const rescheduleMeeting = async (data) => {
    setIsLoading(true);
    await hideModal();
    let schedules = data['schedules'];
    setShowConfirmation(false);
    schedules['reason']= reasonChangeRef.current.value;
    await Axios({
      url: `${propValues.rescheduleURL}?locale=${propValues.languageCode}`,
      method: propValues.method,
      data: {
        'reason':schedules['reason'],
        'timing':schedules['timing'],
        'timezone':schedules['timezone'],
        'duration': schedules['duration']
      }
    })
    .then((response)=>{
        setIsLoading(false);
        if(response.data.status===true){
          setShowConfirmation(true);
          propValues.clearDay();
          propValues.handleConfirmButton(false);
          // props.callbackInfoMessage(response.data.message)
        }
        else {
          // error
          setShowConfirmation(false);
          showError(response.data.message);
        }
    })
    .catch(error=>{
      showError(error.message);
      setIsLoading(false);
    });
  };

  // cancel meeting
  const cancelMeeting = async () => {
    setIsLoading(true);
    await hideModal();
    setShowConfirmation(false);
    await Axios({
      url: `${propValues.cancelURL}?locale=${propValues.languageCode}`,
      method: propValues.method,
      data:{
        'reason': reasonChangeRef.current.value
      }
    })
    .then((response)=>{
      setIsLoading(false);
      if(response.data.status===true){
        setAcceptCancel(true);
        setShowConfirmation(true);
        propValues.hideHeaderText();
        propValues.handleConfirmButton(false);
      } else {
        // error
        setShowConfirmation(false);
        showError(response.data.message);
      }
    })
    .catch(error=>{
      showError(error.message);
      setIsLoading(false);
    });
  };

  const successCreateAppointment = async() => {
    await props.clearDay();
    // Facebook Pixel
    if (propValues.facebookPixel) {
      propValues.facebookPixel.trackCustom('Novocall', {method: 'TimeSync'});
    }
    // Google Analaytics
    if (propValues.googleAnalytics) {
        propValues.googleAnalytics.event({
            category: 'Novocall Conversion',
            action: 'timesync',
            label: propValues.uid
        });
    }
    if (propValues.googleAnalytics4) {
      propValues.googleAnalytics4.event({
          category: 'Novocall Conversion',
          action: 'timesync',
          label: propValues.uid
      });
    }
    // Post message to parent (custom method for iframe)
    window.parent.postMessage('novocallTimeSync', '*');
    propValues.handleConfirmButton(false);
  }

  // generate amount if payment is required
  const amountFormatted = () => {
    if(!propValues.paymentSettings) return 0;

    let amount = 0;
    amount = (Number(propValues.paymentSettings.price)/100).toFixed(2);

    let priceSplit = amount.split(".");
    if(priceSplit.length > 1){
        priceSplit[0] = priceSplit[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    }
    amount = priceSplit.join(".");
    return amount;
  };

  const showToastrError = async (message) => {
    setShowConfirmation(false);
    await showError(message);
  };

  // return schedule created
  const getScheduleCreated = useCallback(() => {
    return scheduleRef.current;
  }, [scheduleRef]);

  /** Load Form */
  const loadForm = () => {
    if(isLoadingPage || !propValues || !propValues.show) return null;

    if(showConfirmation){
      return(
        <Confirmation
          showConfirmation={showConfirmation}
          color={propValues.color}
          handleBackButton={propValues.handleBackAfterConfirmation}
          name={propValues.name}
          timezoneName={propValues.timezoneName}
          times={propValues.times}
          acceptCancel={acceptCancel}
          location={propValues.location}
          redirectStatus={props.redirectStatus}
          redirectLink={props.redirectLink}
          isoTime={propValues.isoTime}
          timeInterval={propValues.timeInterval === 'Ask Invitee' ? propValues.currentDuration : propValues.timeInterval}
          selectedLocation={selectedLocation || propValues.scheduleLocation} />
      )
    }

    if(!showConfirmation && !propValues.isReschedule && !propValues.isCancelMeeting && propValues.acceptPayment === 'stripe' && propValues.chargeDetail?.stripe){
      return (
        <Elements stripe={stripePromise}>
          <PaymentForm
            elements={elements}
            acceptPayment={propValues.acceptPayment}
            paymentSettings={propValues.paymentSettings}
            chargeDetail={props.chargeDetail}
            handleConfirmBackButton={props.handleBackButton}
            formRef={formRef}
            toastList={toastList}
            autoDeleteTime={autoDeleteTime}
            modalConfirmBgColor={modalConfirmBgColor}
            modalConfirmButtonOut={modalConfirmButtonOut}
            modalConfirmButtonOver={modalConfirmButtonOver}
            populateFormData={populateFormData}
            createNewAppointmentAPI={createNewAppointmentAPI}
            changeShowConfirmationComponent={changeShowConfirmationComponent}
            successCreateAppointment={successCreateAppointment}
            showError={showError}
            color={propValues.color}
            buttonFontColor={buttonFontColor}
            handleBackButton={propValues.handleBackButton}
            handleFormSubmitRef={propValues.handleFormSubmitRef}
            showErrorToastr= { showToastrError }
            formURL={propValues.formURL}
            uid={propValues.uid} />
        </Elements>
      );
    }
    else{
      return (
        <Form
          elements={elements}
          acceptPayment={propValues.acceptPayment}
          paymentSettings={propValues.paymentSettings}
          chargeDetail={propValues.chargeDetail}
          handleConfirmBackButton={props.handleBackButton}
          formRef={formRef}
          reasonChangeRef={reasonChangeRef}
          toastList={toastList}
          autoDeleteTime={autoDeleteTime}
          modalConfirmBgColor={modalConfirmBgColor}
          modalConfirmButtonOut={modalConfirmButtonOut}
          modalConfirmButtonOver={modalConfirmButtonOver}
          populateFormData={populateFormData}
          createNewAppointmentAPI={createNewAppointmentAPI}
          changeShowConfirmationComponent={changeShowConfirmationComponent}
          successCreateAppointment={successCreateAppointment}
          showToastrError={showToastrError}
          isLoading={isLoading}
          amountFormatted={amountFormatted}
          isReschedule={propValues.isReschedule}
          isCancelMeeting={propValues.isCancelMeeting}
          submitForm={submitForm}
          formURL={propValues.formURL}
          changeIsLoading={changeIsLoading}
          schedule={schedule}
          getScheduleCreated={getScheduleCreated}
          handleModal={handleModal}
          cancelMeeting={cancelMeeting}
          rescheduleMeeting={rescheduleMeeting}
          handleBackButton={propValues.handleBackButton}
          color={propValues.color}
          buttonFontColor={buttonFontColor}
          handleFormSubmitRef={propValues.handleFormSubmitRef}
          handleConfirmButton={propValues.handleConfirmButton}
          locationOptions={propValues.locationOptions} />
      );
    }
  }
  /** END of Load Form */

  return loadForm();
});

const Form = memo((props) => {
  const [ borderFocusColor, setBorderFocusColor ] = useState(`#${Colors.disabledText}`);
  const { t } = useTranslation();

    const {
      formRef,
      acceptPayment,
      isReschedule,
      isCancelMeeting,
      submitForm,
      elements,
      paymentSettings,
      amountFormatted,
      chargeDetail,
      isLoading,
      populateFormData,
      createNewAppointmentAPI,
      successCreateAppointment,
      changeShowConfirmationComponent,
      formURL,
      showToastrError,
      reasonChangeRef,
      modalConfirmBgColor,
      buttonFontColor,
      color,
      modalConfirmButtonOut,
      modalConfirmButtonOver,
      changeIsLoading,
      schedule,
      getScheduleCreated,
      handleFormSubmitRef,
      handleConfirmButton
    } = props;

    const { width } = useWindowDimensions();

    useEffect(() => {
      if(width <= 858){
        if(acceptPayment === 'paypal'){
          handleConfirmButton(false);
        }
        else{
          handleConfirmButton(true);
        }
      }
    },
    // eslint-disable-next-line
    [width])

    const inputFieldOnFocus = () => {
      setBorderFocusColor(color);
    };

    const inputFieldOnBlur = () => {
      setBorderFocusColor(`#${Colors.disabledText}`);
    };

    useImperativeHandle(handleFormSubmitRef, () => ({
      submitForm(event){
        submitForm(event);
      }
    }));


    return (
      <div className="form-fields-wrap">
        <form
          ref={formRef}
          id="form-submit"
          onSubmit = { (!acceptPayment || ((isReschedule || isCancelMeeting) && acceptPayment)) ? ((e) => submitForm(e)) : undefined }>

          <div className="content-form">
            {(!isReschedule && !isCancelMeeting) ?
              // create new appointment
              (
                <>
                  <div className="header-form">
                    {t('Fill in your details')}
                  </div>

                  { elements }

                  { acceptPayment && Object.keys(chargeDetail).length > 0 && (
                    <div id="payment-meeting">
                      <div className="payments-detail-wrap">
                        <div className="title-payment">
                          {t('Payment Information')}
                        </div>

                        <div className="payments-detail">
                          <PaymentDetail paymentSettings={paymentSettings} amount={amountFormatted} />
                        </div>
                      </div>

                      { acceptPayment === 'paypal' && (
                        <Paypal
                          paymentSettings={paymentSettings}
                          chargeDetail={chargeDetail}
                          isLoadingForm={isLoading}
                          createNewAppointmentAPI = { createNewAppointmentAPI }
                          formURL={formURL}
                          showErrorToastr= { showToastrError }
                          populateFormData={populateFormData}
                          successCreateAppointment={successCreateAppointment}
                          changeShowConfirmationComponent={changeShowConfirmationComponent}
                          changeIsLoading={changeIsLoading}
                          schedule={schedule}
                          getScheduleCreated={getScheduleCreated} />
                      )}
                    </div>
                  )}
                </>
              )
              : /* reschedule or cancel */ (
                <>
                  <div className="header-form">
                    { `${isReschedule ? t('Reschedule') : t('Cancel')} ${t('Meeting')}` }
                  </div>
                  <div className="subtitle">
                    { t('Add a message') }
                  </div>
                  <div className="input-nav field-label">
                    <div className="field-input">
                      <textarea
                        required={true}
                        ref={reasonChangeRef}
                        className="input"
                        style={{ borderColor: borderFocusColor }}
                        onFocus={() => inputFieldOnFocus()}
                        onBlur={() => inputFieldOnBlur()}
                        rows="5"
                        placeholder={t('Type your reasons here')}>
                      </textarea>
                    </div>
                  </div>
                </>
            )}
          </div>

          {/* <button type="button" onClick={() => handleModal('Test Content', 'Test Title', isReschedule ? rescheduleMeeting : cancelMeeting, { cancel: 'No', submit: 'Yes' } )} >Test Open Modal</button> */}

          <div className="button-confirm-wrap">
            { ( (width > 858) && ((isReschedule || isCancelMeeting) || acceptPayment !== "paypal")) && (

                <input
                  type="submit"
                  value={isLoading ? t("Loading") : (isReschedule || isCancelMeeting) ? t("Confirm") : t("Submit")}
                  className="confirm-button"
                  style={{backgroundColor: modalConfirmBgColor, borderColor: color, color: buttonFontColor }}
                  onMouseOver={modalConfirmButtonOver}
                  onMouseOut={modalConfirmButtonOut}
                  disabled={isLoading} />
            ) }

            {/* { width > 858 && (
              <button
                type="button"
                onClick={() => handleBackButton()}
                className="back-button"
                style={{ color: color }} >
                { t('Go Back') }
              </button>
            )} */}
          </div>
        </form>

      </div>
    );
});

export default (FormField);
